import { Box, ThemeProvider, createTheme } from '@mui/material';
import { Component, ComponentClass, ReactInstance, createRef } from 'react';
import { ReactToPrint } from 'react-to-print';
import { palette } from '../../../config/theme/theme';
import { BasePrintControllerProps } from '../common/controllers/BasePrintController';
import { PRINT_PAGE_STYLE, PRINT_WIDTH, SCALE } from '../constants/constants';

type PrintWrapperProps<T extends BasePrintControllerProps> = {
  // The component to be printed
  Component: ComponentClass<T>;

  // Props for the component. Inherited props from `ExportControllerProps`
  // should not be passed here as they will be assigned from `ReactPrintWrapper`
  componentProps: Omit<T, keyof BasePrintControllerProps>;

  // Callback fired after the print dialog has been opened
  onDone: () => void;

  // Callback fired in case the component fails to load.
  onError: (e: unknown) => void;

  // File name to use as default suggestion in the browser print window
  printTitle: string;
};

type PrintWrapperState = {
  // Optional title of the printed component.
  // This title will temporarily replace the current page title
  // and will become the default name of the PDF file saved
  // from the print dialog.
  documentTitle?: string;
};

// Theme applied to the printed component. The global theme
// cannot be used as it also defines additional props and default
// component overrides that may not be available from within
// `ReactPrintWrapper` (for example, `MuiLink.defaultProps.component`
// uses `A11yLink`, which requires the component to be wrapped in a Router).
// The theme overrides specific styles and props to improve the printed layout
// across browsers.
const PRINT_THEME = createTheme({
  palette,
  components: {
    MuiPaper: { defaultProps: { variant: 'outlined' } },
    MuiButtonBase: { defaultProps: { disableRipple: true, disableTouchRipple: true } },
    MuiCheckbox: {
      styleOverrides: { root: { padding: 0, margin: '0.5rem', width: '1.5rem', height: '1.5rem', position: 'relative' } },
      defaultProps: { sx: { '& .MuiSvgIcon-root': { width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 } } },
    },
    MuiSvgIcon: { defaultProps: { sx: { width: '1rem', height: '1rem' } } },
    MuiLink: { defaultProps: { underline: 'none' } },
  },
});

export class ReactPrintWrapper<T extends BasePrintControllerProps> extends Component<PrintWrapperProps<T>, PrintWrapperState> {
  componentRef: ReactInstance | null = null;
  printRef = createRef<ReactToPrint>();

  constructor(props: PrintWrapperProps<T>) {
    super(props);

    this.state = { documentTitle: undefined };
  }

  printContent = () => {
    return this.componentRef;
  };

  setComponentRef = (ref: unknown) => {
    this.componentRef = ref as ReactInstance;
  };

  onRendered = () => {
    // A non-zero timeout prevents Safari and Firefox from skipping the `setTimeout`,
    // which would cause the print dialog to be displayed before the content is
    // actually rendered in the page
    const SAFETY_TIMEOUT = 1;
    setTimeout(() => {
      this.setState({ documentTitle: this.props.printTitle });
      this.printRef.current?.handleClick();
    }, SAFETY_TIMEOUT);
  };

  onDone = () => {
    this.props.onDone();
  };

  onError = (e: unknown) => {
    this.props.onError(e);
  };

  render() {
    const { Component, componentProps } = this.props;

    const props = { ...componentProps, onRendered: this.onRendered, onError: this.onError } as T;

    return (
      <ThemeProvider theme={PRINT_THEME}>
        <ReactToPrint
          ref={this.printRef}
          content={this.printContent}
          onAfterPrint={this.onDone}
          pageStyle={PRINT_PAGE_STYLE}
          documentTitle={this.state.documentTitle}
          copyStyles
          removeAfterPrint
        />
        <Box ref={this.setComponentRef} sx={{ width: `${PRINT_WIDTH / SCALE}pt` }}>
          <Component {...props} />
        </Box>
      </ThemeProvider>
    );
  }
}
